import React, {useEffect} from 'react';
import {getStore} from 'src/v2/state/store';
import Loading from 'src/components/core/Loading';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {AnimatePresence, motion} from 'framer-motion';
import {useStateInitialize} from 'src/v2/state/useStateInitialize';
import {Layout} from 'src/v2/components/screen/Layout';
import {ScreenManager as RevampedScreenManager} from 'src/v2/components/screen/ScreenManager';
import {WalletConnectionClientProvider} from 'src/v2/state/walletConnection';
import {setupInContextMessageListener} from 'src/v2/util/inContextCompletion';

/**
 * V2Provider handles critical initialization for the v2 app.
 * This wrapper guarantees its children that:
 * - sessionModel is loaded and not null
 * - wallet connection iframe is ready and initialized
 */
const V2Provider = ({children}: {children: React.ReactNode}) => {
  const payinSessionContext = useSessionContext();
  const store = getStore();

  useStateInitialize(payinSessionContext);

  const isSessionLoaded = !!store.useSelector((state) => {
    return state.id.length > 0;
  });
  const isWalletConnectionReady = store.useSelector((state) => {
    return state.walletConnection.ready;
  });

  if (!isSessionLoaded) {
    return <Loading />;
  }

  return (
    <WalletConnectionClientProvider>
      {isWalletConnectionReady ? children : <Loading />}
    </WalletConnectionClientProvider>
  );
};

const App = () => {
  useEffect(() => {
    return setupInContextMessageListener();
  }, []);

  return (
    <V2Provider>
      <AnimatePresence>
        <motion.div
          key="loaded"
          animate={{opacity: 1}}
          initial={{opacity: 0}}
          transition={{ease: 'easeIn', duration: 0.15}}
        >
          <Layout>
            <RevampedScreenManager />
          </Layout>
        </motion.div>
      </AnimatePresence>
    </V2Provider>
  );
};

export default App;
